import { useCallback, useEffect, useRef, useState } from "react";
import { useIsMutating, useQueryClient, type QueryKey } from "@tanstack/react-query";

import { AUTO_REFRESH_DEFAULTS, EAutoRefreshSuspendReason } from "@/constants/auto-refresh.constants";
import { isDefined } from "@/lib/common";
import { isWindow } from "@/lib/utils";

export interface IUseAutoRefreshOptions {
  /** Countdown length in whole seconds. Declared per page, not baked into this hook. */
  intervalSeconds: number;
  /** Query keys to invalidate on each tick — every active match refetches in the background. */
  queryKeys: QueryKey[];
  /** Master on/off switch, independent of the manual pause toggle. Defaults to `true`. */
  enabled?: boolean;
  /** Any additional reason to suspend (e.g. a dialog/drawer/sheet open elsewhere on the page). */
  isSuspendedExternally?: boolean;
}

export interface IUseAutoRefreshResult {
  secondsRemaining: number;
  /** 1 (just reset) draining down to 0 (about to refresh) — drives the countdown ring. */
  progress: number;
  isRefreshing: boolean;
  /** `true` only when the user toggled the pill's pause button — session-only, never persisted. */
  isPaused: boolean;
  /** `true` while auto-refresh is suspended for any reason other than the manual pause toggle. */
  isSuspended: boolean;
  suspendReason: EAutoRefreshSuspendReason | undefined;
  togglePause: () => void;
  refreshNow: () => void;
}

const getIsDocumentHidden = (): boolean => (isWindow() ? document.hidden : false);

/**
 * Silent TanStack Query refetch on a per-page interval: countdown/ring state plus automatic
 * suspension while the tab is hidden/blurred, a mutation is in flight, or the caller reports an
 * external reason (e.g. an open dialog via `useSuspendAutoRefresh`). Never calls
 * `window.location.reload()` — invalidated queries refetch in place so scroll position, open
 * dialogs, and table state all survive.
 */
export const useAutoRefresh = (options: IUseAutoRefreshOptions): IUseAutoRefreshResult => {
  const {
    intervalSeconds,
    queryKeys,
    enabled = AUTO_REFRESH_DEFAULTS.ENABLED,
    isSuspendedExternally = false,
  } = options;

  const queryClient = useQueryClient();
  const isMutating = useIsMutating() > 0;

  const [isPaused, setIsPaused] = useState(false);
  const [isTabHidden, setIsTabHidden] = useState(getIsDocumentHidden);
  const [isWindowBlurred, setIsWindowBlurred] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(intervalSeconds);
  const [progress, setProgress] = useState(1);

  const queryKeysRef = useRef(queryKeys);
  queryKeysRef.current = queryKeys;

  const deadlineRef = useRef(Date.now() + intervalSeconds * 1000);
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    if (!isWindow()) return;

    const handleVisibilityChange = () => setIsTabHidden(document.hidden);
    const handleBlur = () => setIsWindowBlurred(true);
    const handleFocus = () => setIsWindowBlurred(false);

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  const resetCountdown = useCallback(() => {
    deadlineRef.current = Date.now() + intervalSeconds * 1000;
    setSecondsRemaining(intervalSeconds);
    setProgress(1);
  }, [intervalSeconds]);

  const runRefresh = useCallback(async () => {
    if (isRefreshingRef.current) return;

    isRefreshingRef.current = true;
    setIsRefreshing(true);
    try {
      await Promise.all(
        queryKeysRef.current.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
    } finally {
      isRefreshingRef.current = false;
      setIsRefreshing(false);
      resetCountdown();
    }
  }, [queryClient, resetCountdown]);

  const suspendReason: EAutoRefreshSuspendReason | undefined = isTabHidden
    ? EAutoRefreshSuspendReason.TAB_HIDDEN
    : isWindowBlurred
      ? EAutoRefreshSuspendReason.WINDOW_BLURRED
      : isMutating
        ? EAutoRefreshSuspendReason.MUTATION_IN_FLIGHT
        : isSuspendedExternally
          ? EAutoRefreshSuspendReason.DIALOG_OPEN
          : undefined;

  const isSuspended = isDefined(suspendReason);
  const isBlocked = !enabled || isPaused || isSuspended || isRefreshing;

  // Restart the countdown fresh whenever a block clears, rather than resuming a stale deadline.
  const wasBlockedRef = useRef(isBlocked);
  useEffect(() => {
    if (wasBlockedRef.current && !isBlocked) {
      resetCountdown();
    }
    wasBlockedRef.current = isBlocked;
  }, [isBlocked, resetCountdown]);

  useEffect(() => {
    if (isBlocked) return;

    const tick = () => {
      const remainingMs = deadlineRef.current - Date.now();
      if (remainingMs <= 0) {
        void runRefresh();
        return;
      }
      setSecondsRemaining(Math.max(0, Math.ceil(remainingMs / 1000)));
      setProgress(Math.min(1, Math.max(0, remainingMs / (intervalSeconds * 1000))));
    };

    tick();
    const intervalId = window.setInterval(tick, AUTO_REFRESH_DEFAULTS.TICK_MS);
    return () => window.clearInterval(intervalId);
  }, [isBlocked, intervalSeconds, runRefresh]);

  const togglePause = useCallback(() => setIsPaused((current) => !current), []);
  const refreshNow = useCallback(() => void runRefresh(), [runRefresh]);

  return {
    secondsRemaining,
    progress,
    isRefreshing,
    isPaused,
    isSuspended,
    suspendReason,
    togglePause,
    refreshNow,
  };
};
